import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './AppBar.module.scss';
import { FiCountry, FiLanguage, useLocale } from '../../contexts/LocaleContext';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from '../../types';
import Twemoji from 'react-twemoji';

function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!ref.current || !target || ref.current.contains(target)) return;
      handler();
    };
    document.addEventListener('pointerdown', listener);
    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [ref, handler]);
}

function useCloseOnScroll(open: boolean, handler: () => void) {
  useEffect(() => {
    if (!open) return;
    const onScroll = () => handler();
    window.addEventListener('scroll', onScroll, { capture: true, passive: true } as EventListenerOptions);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, handler]);
}

export default function CountrySelector() {
  const { country, setCountry, language, setLanguage, flagEmoji } = useLocale();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const internalTester = useSelector((state: AppState) => !!state.config.siteConfig.internalTester);

  const fiSupportedCountries = useMemo(
    () =>
      getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [FiCountry.US, FiCountry.CA], {
        internalTester,
      }),
    [internalTester],
  );

  const showSelector = fiSupportedCountries.length > 1;

  useOnClickOutside(menuRef, () => setOpen(false));
  useCloseOnScroll(open, () => setOpen(false));

  const { t } = useTranslation();

  const gbEnabled = fiSupportedCountries.includes(FiCountry.GB);

  const options = gbEnabled
    ? ([
        { key: 'US', label: t('united_states_of_america'), emoji: '🇺🇸' },
        { key: 'en-CA', label: t('canada_english'), emoji: '🇨🇦' },
        { key: 'fr-CA', label: t('canada_french'), emoji: '🇨🇦' },
        { key: 'GB', label: t('united_kingdom'), emoji: '🇬🇧' },
      ] as const)
    : ([
        { key: 'US', label: t('united_states_of_america'), emoji: '🇺🇸' },
        { key: 'en-CA', label: t('canada_english'), emoji: '🇨🇦' },
        { key: 'fr-CA', label: t('canada_french'), emoji: '🇨🇦' },
      ] as const);
  const handleSelect = useCallback(
    (key: string) => {
      if (key === 'US') {
        setCountry(FiCountry.US);
        setLanguage(FiLanguage.EN_US);
      } else if (key === 'en-CA') {
        setCountry(FiCountry.CA);
        setLanguage(FiLanguage.EN_CA);
      } else if (key === 'fr-CA') {
        setCountry(FiCountry.CA);
        setLanguage(FiLanguage.FR_CA);
      } else if (key === 'GB') {
        setCountry(FiCountry.GB);
        setLanguage(FiLanguage.EN_GB);
      }
      setOpen(false);
    },
    [setCountry, setLanguage],
  );

  if (!showSelector) return null;

  return (
    <div className={styles.countrySelector} ref={menuRef}>
      <button
        type="button"
        className={styles.countryButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Twemoji options={{ className: styles.flagIcon }}>{flagEmoji}</Twemoji>
      </button>
      {open && (
        <div className={classNames(styles.countryMenu)} role="menu">
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={classNames(styles.countryMenuItem, {
                [styles.selected]:
                  opt.key === 'US'
                    ? country === FiCountry.US
                    : opt.key === 'en-CA'
                    ? country === FiCountry.CA && language === FiLanguage.EN_CA
                    : opt.key === 'fr-CA'
                    ? country === FiCountry.CA && language === FiLanguage.FR_CA
                    : country === FiCountry.GB && language === FiLanguage.EN_GB,
              })}
              onClick={() => handleSelect(opt.key as string)}
              role="menuitem"
            >
              <Twemoji options={{ className: styles.flagIcon }}>{opt.emoji}</Twemoji>
              <span className={styles.countryMenuLabel}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
